import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, Device } from '../../services/api_url';
import { useUserData } from '../../services/useUserData';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import { BackIcon } from '../../assets/images/BackIcon';
import { ProtocolIcon } from '../../assets/images/DeviceIcons';
import './PaymentDevicesPage.scss';

interface PeriodOption {
    months: number;
    name: string;
    popular?: boolean;
}

interface SelectedDevice {
    id: number;
    name: string | null;
    protocol_name: string | null;
    expires_at: string;
    is_configured: boolean;
}

interface PaymentDevicesPageProps {
    onModalStateChange?: (isOpen: boolean) => void;
}

type AppEnv = Record<string, string | number | undefined>;
type PeriodPriceMap = Record<number, number>;

interface PricingConfig {
    oneDevicePeriodPrices: PeriodPriceMap;
    threeDevicePeriodPrices: PeriodPriceMap;
    baseMonthPrice: number;
}

const PERIOD_OPTIONS: PeriodOption[] = [
    { months: 1, name: '1 месяц' },
    { months: 3, name: '3 месяца' },
    { months: 6, name: '6 месяцев', popular: true },
    { months: 12, name: '12 месяцев' }
];

const getRuntimeEnv = (): AppEnv | undefined => {
    return (window as Window & { __APP_ENV__?: AppEnv }).__APP_ENV__;
};

const BUILD_ENV: Record<string, string | undefined> = {
    PAYMENT_1M_1_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_1M_1_DEVICE_PRICE,
    PAYMENT_3M_1_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_3M_1_DEVICE_PRICE,
    PAYMENT_6M_1_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_6M_1_DEVICE_PRICE,
    PAYMENT_12M_1_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_12M_1_DEVICE_PRICE,

    PAYMENT_1M_3_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_1M_3_DEVICE_PRICE,
    PAYMENT_3M_3_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_3M_3_DEVICE_PRICE,
    PAYMENT_6M_3_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_6M_3_DEVICE_PRICE,
    PAYMENT_12M_3_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_12M_3_DEVICE_PRICE,

    CONF_1M_1_DEVICE_PRICE: process.env.REACT_APP_CONF_1M_1_DEVICE_PRICE,
    CONF_3M_1_DEVICE_PRICE: process.env.REACT_APP_CONF_3M_1_DEVICE_PRICE,
    CONF_6M_1_DEVICE_PRICE: process.env.REACT_APP_CONF_6M_1_DEVICE_PRICE,
    CONF_12M_1_DEVICE_PRICE: process.env.REACT_APP_CONF_12M_1_DEVICE_PRICE,

    CONF_1M_3_DEVICE_PRICE: process.env.REACT_APP_CONF_1M_3_DEVICE_PRICE,
    CONF_3M_3_DEVICE_PRICE: process.env.REACT_APP_CONF_3M_3_DEVICE_PRICE,
    CONF_6M_3_DEVICE_PRICE: process.env.REACT_APP_CONF_6M_3_DEVICE_PRICE,
    CONF_12M_3_DEVICE_PRICE: process.env.REACT_APP_CONF_12M_3_DEVICE_PRICE
};

const parseRequiredEnvNumber = (rawValue: unknown, envKey: string): number => {
    const numericValue = Number(rawValue);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        throw new Error(`Некорректное значение env: ${envKey}`);
    }

    return numericValue;
};

const readRequiredPriceEnv = (keys: string[]): number => {
    const runtimeEnv = getRuntimeEnv();

    for (const key of keys) {
        const runtimeValue = runtimeEnv?.[key];
        if (runtimeValue !== undefined && runtimeValue !== null && `${runtimeValue}`.trim() !== '') {
            return parseRequiredEnvNumber(runtimeValue, key);
        }

        const buildValue = BUILD_ENV[key];
        if (buildValue !== undefined && buildValue !== null && buildValue.trim() !== '') {
            return parseRequiredEnvNumber(buildValue, key);
        }
    }

    throw new Error(`Не найдена env-переменная: ${keys.join(' / ')}`);
};

const resolvePricingConfig = (): PricingConfig => {
    const oneDevicePeriodPrices: PeriodPriceMap = {
        1: readRequiredPriceEnv(['PAYMENT_1M_1_DEVICE_PRICE', 'CONF_1M_1_DEVICE_PRICE']),
        3: readRequiredPriceEnv(['PAYMENT_3M_1_DEVICE_PRICE', 'CONF_3M_1_DEVICE_PRICE']),
        6: readRequiredPriceEnv(['PAYMENT_6M_1_DEVICE_PRICE', 'CONF_6M_1_DEVICE_PRICE']),
        12: readRequiredPriceEnv(['PAYMENT_12M_1_DEVICE_PRICE', 'CONF_12M_1_DEVICE_PRICE'])
    };

    const threeDevicePeriodPrices: PeriodPriceMap = {
        1: readRequiredPriceEnv(['PAYMENT_1M_3_DEVICE_PRICE', 'CONF_1M_3_DEVICE_PRICE']),
        3: readRequiredPriceEnv(['PAYMENT_3M_3_DEVICE_PRICE', 'CONF_3M_3_DEVICE_PRICE']),
        6: readRequiredPriceEnv(['PAYMENT_6M_3_DEVICE_PRICE', 'CONF_6M_3_DEVICE_PRICE']),
        12: readRequiredPriceEnv(['PAYMENT_12M_3_DEVICE_PRICE', 'CONF_12M_3_DEVICE_PRICE'])
    };

    return {
        oneDevicePeriodPrices,
        threeDevicePeriodPrices,
        baseMonthPrice: oneDevicePeriodPrices[1]
    };
};

const roundPrettyPrice = (value: number): number => {
    if (value <= 0) return 0;

    if (value < 200) {
        return Math.round(value);
    }

    if (value < 1000) {
        return Math.max(99, Math.round((value + 1) / 10) * 10 - 1);
    }

    if (value < 3000) {
        return Math.max(99, Math.round((value + 1) / 50) * 50 - 1);
    }

    return Math.max(99, Math.round((value + 1) / 100) * 100 - 1);
};

const clamp = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
};

const getSingleDevicePeriodPrice = (months: number, oneDevicePeriodPrices: PeriodPriceMap): number => {
    return oneDevicePeriodPrices[months] ?? 0;
};

const getThreeDeviceTargetPrice = (months: number, threeDevicePeriodPrices: PeriodPriceMap): number => {
    return threeDevicePeriodPrices[months] ?? 0;
};

const getAnchorFactor = (
    months: number,
    oneDevicePeriodPrices: PeriodPriceMap,
    threeDevicePeriodPrices: PeriodPriceMap
): number => {
    const singlePrice = getSingleDevicePeriodPrice(months, oneDevicePeriodPrices);
    const threePrice = getThreeDeviceTargetPrice(months, threeDevicePeriodPrices);

    if (!singlePrice || !threePrice) {
        return 1;
    }

    return threePrice / (singlePrice * 3);
};

const getQuantityFactor = (
    months: number,
    count: number,
    oneDevicePeriodPrices: PeriodPriceMap,
    threeDevicePeriodPrices: PeriodPriceMap
): number => {
    if (count <= 1) {
        return 1;
    }

    const anchorFactor = getAnchorFactor(months, oneDevicePeriodPrices, threeDevicePeriodPrices);

    if (count <= 3) {
        const progress = (count - 1) / 2;
        const curvedProgress = Math.pow(progress, 0.85);
        return clamp(1 - (1 - anchorFactor) * curvedProgress, 0.42, 1);
    }

    const extraDevices = count - 3;
    const extraDiscount =
        Math.min(extraDevices, 4) * 0.025 +
        Math.max(extraDevices - 4, 0) * 0.015;

    return clamp(anchorFactor - extraDiscount, 0.42, 1);
};

const getDisplayPrice = (
    months: number,
    count: number,
    oneDevicePeriodPrices: PeriodPriceMap,
    threeDevicePeriodPrices: PeriodPriceMap
): number => {
    if (count <= 0) return 0;

    const singlePrice = getSingleDevicePeriodPrice(months, oneDevicePeriodPrices);

    if (!singlePrice) {
        return 0;
    }

    if (count === 1) {
        return singlePrice;
    }

    if (count === 3) {
        return getThreeDeviceTargetPrice(months, threeDevicePeriodPrices);
    }

    const quantityFactor = getQuantityFactor(
        months,
        count,
        oneDevicePeriodPrices,
        threeDevicePeriodPrices
    );

    return roundPrettyPrice(singlePrice * count * quantityFactor);
};

const getOldPrice = (months: number, count: number, baseMonthPrice: number): number => {
    return Math.round(baseMonthPrice * months * count);
};

const getDiscountPercent = (currentPrice: number, oldPrice: number): number => {
    if (oldPrice <= 0 || currentPrice >= oldPrice) {
        return 0;
    }

    return Math.max(0, Math.round((1 - currentPrice / oldPrice) * 100));
};

function PaymentDevicesPage({ onModalStateChange }: PaymentDevicesPageProps) {
    const navigate = useNavigate();
    const { telegramId, loading: userLoading, isFromTelegram } = useUserData();

    const [devices, setDevices] = useState<Device[]>([]);
    const [devicesLoading, setDevicesLoading] = useState(true);
    const [deviceLimit, setDeviceLimit] = useState(0);
    const [pageReady, setPageReady] = useState(false);

    const [selectedDevices, setSelectedDevices] = useState<SelectedDevice[]>([]);
    const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption | null>(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('card');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [hasNoDevices, setHasNoDevices] = useState<boolean>(false);

    const [promoCode, setPromoCode] = useState<string>('');
    const [promoApplied, setPromoApplied] = useState<boolean>(false);
    const [promoMessage, setPromoMessage] = useState<string>('');
    const [promoDiscount, setPromoDiscount] = useState<number>(0);
    const [finalPrice, setFinalPrice] = useState<number>(0);
    const [applyingPromo, setApplyingPromo] = useState<boolean>(false);
    const [extraDevices, setExtraDevices] = useState<number>(0);

    const isPageLoading = userLoading || (devicesLoading && !pageReady);

    const pricingConfig = useMemo(() => {
        try {
            return {
                data: resolvePricingConfig(),
                error: null as string | null
            };
        } catch (error) {
            return {
                data: null as PricingConfig | null,
                error: error instanceof Error ? error.message : 'Ошибка конфигурации цен'
            };
        }
    }, []);

    const baseMonthPrice = pricingConfig.data?.baseMonthPrice ?? 0;
    const oneDevicePeriodPrices = pricingConfig.data?.oneDevicePeriodPrices ?? {};
    const threeDevicePeriodPrices = pricingConfig.data?.threeDevicePeriodPrices ?? {};

    useEffect(() => {
        const loadDevicesData = async () => {
            if (!telegramId) {
                setDevicesLoading(false);
                return;
            }

            setDevicesLoading(true);
            try {
                const response = await apiService.getUserDevices(telegramId);
                if (response?.success) {
                    setDevices(response.devices || []);
                    setDeviceLimit(response.device_limit || 0);
                    setHasNoDevices(response.total === 0);
                }
            } catch (error) {
                console.error('Ошибка загрузки устройств:', error);
            } finally {
                setDevicesLoading(false);
                setPageReady(true);
            }
        };

        loadDevicesData();
    }, [telegramId]);

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }
    }, []);

    const sortedDevices = useMemo(() => {
        return [...devices].sort((a, b) => {
            const now = Date.now();
            const aTimeLeft = new Date(a.expires_at).getTime() - now;
            const bTimeLeft = new Date(b.expires_at).getTime() - now;

            return aTimeLeft - bTimeLeft;
        });
    }, [devices]);

    const toggleDeviceSelection = useCallback((device: Device) => {
        setSelectedDevices((prev) => {
            const exists = prev.find((d) => d.id === device.id);
            if (exists) {
                return prev.filter((d) => d.id !== device.id);
            }

            return [
                ...prev,
                {
                    id: device.id,
                    name: device.name,
                    protocol_name: device.protocol_name,
                    expires_at: device.expires_at,
                    is_configured: device.is_configured
                }
            ];
        });
    }, []);

    const selectAllDevices = useCallback(() => {
        setSelectedDevices(
            sortedDevices.map((device) => ({
                id: device.id,
                name: device.name,
                protocol_name: device.protocol_name,
                expires_at: device.expires_at,
                is_configured: device.is_configured
            }))
        );
    }, [sortedDevices]);

    const clearSelection = useCallback(() => {
        setSelectedDevices([]);
    }, []);

    const calculateTotalPrice = useCallback((): number => {
        if (!selectedPeriod || !pricingConfig.data) return 0;

        const totalDevices = selectedDevices.length + extraDevices;

        return getDisplayPrice(
            selectedPeriod.months,
            totalDevices,
            oneDevicePeriodPrices,
            threeDevicePeriodPrices
        );
    }, [
        selectedPeriod,
        selectedDevices.length,
        extraDevices,
        pricingConfig.data,
        oneDevicePeriodPrices,
        threeDevicePeriodPrices
    ]);

    const getProtocolDisplayName = useCallback((protocol?: string | null) => {
        if (!protocol) return '';
        const normalized = protocol.trim().toLowerCase();

        if (normalized === 'awg') return 'AmneziaWG';
        if (normalized === 'wireguard') return 'WireGuard';

        return protocol;
    }, []);

    const formatDate = useCallback((dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }, []);

    const getDeviceVariant = useCallback((device: Device): 'green' | 'yellow' => {
        const expiresAt = new Date(device.expires_at);
        const now = new Date();
        const isExpired = expiresAt < now;

        if (!device.is_configured || isExpired) {
            return 'yellow';
        }

        return 'green';
    }, []);

    const getDeviceStatus = useCallback((device: Device): string => {
        const expiresAt = new Date(device.expires_at);
        const now = new Date();
        const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (!device.is_configured) {
            if (daysLeft < 0) return 'Требуется настройка • Подписка истекла';
            if (daysLeft === 0) return 'Требуется настройка • Заканчивается сегодня';
            return `Требуется настройка • Осталось ${daysLeft} дн.`;
        }

        if (daysLeft < 0) return 'Подписка истекла';
        if (daysLeft === 0) return 'Заканчивается сегодня';
        return `Осталось ${daysLeft} дн.`;
    }, []);

    const getDeviceName = useCallback((device: Device): string => {
        if (device.name) return device.name;
        if (!device.is_configured) return 'Ожидает настройки';
        if (device.protocol_name) return `${getProtocolDisplayName(device.protocol_name)} устройство`;
        return 'Устройство';
    }, [getProtocolDisplayName]);

    const getDeviceTimeInfo = useCallback((device: Device): string => {
        const expiresAt = new Date(device.expires_at);
        const isExpired = expiresAt < new Date();
        return `${isExpired ? 'истек' : 'до'} ${formatDate(device.expires_at)}`;
    }, [formatDate]);

    const handleApplyPromo = useCallback(async () => {
        if (!promoCode.trim() || !telegramId) {
            setPromoMessage('Введите промокод');
            return;
        }

        setApplyingPromo(true);
        setPromoMessage('');

        try {
            const totalPrice = calculateTotalPrice();
            const response = await apiService.applyPromoPayment({
                user_id: telegramId,
                promo_code: promoCode,
                amount: totalPrice
            });

            if (response.success) {
                setPromoApplied(true);
                setPromoMessage(response.message);
                setFinalPrice(
                    response.new_amount !== undefined && response.new_amount !== null
                        ? response.new_amount
                        : totalPrice
                );
                setPromoDiscount(response.discount || 0);
            } else {
                setPromoApplied(false);
                setPromoMessage(response.message);
                setFinalPrice(totalPrice);
                setPromoDiscount(0);
            }
        } catch (error) {
            setPromoMessage('Ошибка при проверке промокода');
            setPromoApplied(false);
        } finally {
            setApplyingPromo(false);
        }
    }, [promoCode, telegramId, calculateTotalPrice]);

    const handleContinueToPayment = useCallback(() => {
        if (selectedDevices.length === 0) {
            alert('Выберите хотя бы одно устройство для продления');
            return;
        }

        if (!selectedPeriod) {
            alert('Выберите срок подписки');
            return;
        }

        if (!telegramId) {
            alert('Ошибка авторизации');
            navigate('/key');
            return;
        }

        setShowPaymentModal(true);
        onModalStateChange?.(true);
    }, [selectedDevices.length, selectedPeriod, telegramId, navigate, onModalStateChange]);

    const handleCloseModal = useCallback(() => {
        setShowPaymentModal(false);
        onModalStateChange?.(false);
        setPaymentError(null);
        setPromoCode('');
        setPromoApplied(false);
        setPromoMessage('');
        setPromoDiscount(0);
        setFinalPrice(calculateTotalPrice());
    }, [onModalStateChange, calculateTotalPrice]);

    const handleCryptoPay = useCallback(async () => {
        if (!telegramId || !selectedPeriod || selectedDevices.length === 0) return;

        setProcessing(true);
        setPaymentError(null);

        try {
            const totalPrice = promoApplied ? finalPrice : calculateTotalPrice();
            const response = await apiService.createCryptoPayment({
                user_id: telegramId,
                device_count: selectedDevices.length,
                months: selectedPeriod.months,
                amount: totalPrice,
                network: 'TRX',
                currency: 'USDT'
            });

            if (response.success && response.heleket_response?.result?.url) {
                window.location.href = response.heleket_response.result.url;
            } else {
                setPaymentError('Ошибка при создании платежа');
            }
        } catch (error) {
            setPaymentError('Ошибка при создании крипто-платежа');
        } finally {
            setProcessing(false);
        }
    }, [telegramId, selectedPeriod, selectedDevices.length, promoApplied, finalPrice, calculateTotalPrice]);

    const handlePay = useCallback(async () => {
        if (selectedPaymentMethod === 'crypto') {
            await handleCryptoPay();
            return;
        }

        if (!telegramId || !selectedPeriod || selectedDevices.length === 0) return;

        setProcessing(true);
        setPaymentError(null);

        try {
            const normalizedPromoCode =
                promoApplied && promoCode.trim()
                    ? promoCode.trim().toUpperCase()
                    : undefined;

            const paymentResponse = await apiService.createPaymentExtend({
                user_id: telegramId,
                device_ids: selectedDevices.map((device) => device.id),
                months: selectedPeriod.months,
                payment_method: selectedPaymentMethod,
                promo_code: normalizedPromoCode,
                extra_devices: extraDevices
            });

            if (paymentResponse?.payment_url) {
                if (selectedPaymentMethod === 'sbp') {
                    if (isFromTelegram) {
                        const tg = (window as any).Telegram?.WebApp;

                        if (tg) {
                            tg.openLink(paymentResponse.payment_url);
                        }
                        handleCloseModal();
                        navigate(`/payment/success`, {
                            state: {
                                payment_id: paymentResponse.payment_id
                            }
                        });
                    } else {
                        // В обычном браузере - обычный редирект
                        window.location.href = paymentResponse.payment_url;
                    }
                } else {
                    window.location.href = paymentResponse.payment_url;
                }
            } else {
                setPaymentError('Ошибка создания платежа');
            }
        } catch (error) {
            setPaymentError('Ошибка при создании платежа');
        } finally {
            setProcessing(false);
        }
    }, [selectedPaymentMethod, telegramId, selectedPeriod, selectedDevices, promoApplied, promoCode, handleCryptoPay]);

    const totalPrice = useMemo(() => calculateTotalPrice(), [calculateTotalPrice]);
    const selectedCount = selectedDevices.length;
    const displayPrice = promoApplied ? finalPrice : totalPrice;
    const selectableAll = selectedCount === sortedDevices.length && sortedDevices.length > 0;

    const selectedCountLabel = useMemo(() => {
        const mod10 = selectedCount % 10;
        const mod100 = selectedCount % 100;

        if (mod10 === 1 && mod100 !== 11) return 'устройство';
        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'устройства';
        return 'устройств';
    }, [selectedCount]);

    const selectedPeriodOldPrice = useMemo(() => {
        if (!selectedPeriod || selectedCount === 0 || !pricingConfig.data) return 0;
        return getOldPrice(selectedPeriod.months, selectedCount, baseMonthPrice);
    }, [selectedPeriod, selectedCount, pricingConfig.data, baseMonthPrice]);

    const selectedPeriodTariffDiscount = useMemo(() => {
        if (!selectedPeriod || selectedCount === 0) return 0;
        return getDiscountPercent(totalPrice, selectedPeriodOldPrice);
    }, [selectedPeriod, selectedCount, totalPrice, selectedPeriodOldPrice]);

    useEffect(() => {
        if (!promoApplied) {
            setFinalPrice(totalPrice);
            return;
        }

        setPromoApplied(false);
        setPromoMessage('');
        setPromoDiscount(0);
        setFinalPrice(totalPrice);
    }, [selectedPeriod?.months, selectedCount, extraDevices, totalPrice]);

    const getPaymentMethodIcon = useCallback((method: string): React.ReactElement => {
        switch (method) {
            case 'card':
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <path d="M22 10H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M6 14H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M12 14H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                );
            case 'sbp':
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <path d="M22 10H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M6 14H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M12 14H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                );
            case 'crypto':
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <path d="M12 6V8M12 16V18M8 12H6M18 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M12 8C9.79 8 8 9.79 8 12C8 14.21 9.79 16 12 16C14.21 16 16 14.21 16 12C16 9.79 14.21 8 12 8Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                );
            default:
                return <span>💳</span>;
        }
    }, []);

    const getPaymentMethodName = useCallback((method: string): string => {
        switch (method) {
            case 'card':
                return 'Карта';
            case 'sbp':
                return 'СБП';
            case 'crypto':
                return 'Крипта';
            default:
                return method;
        }
    }, []);

    const LoaderIcon = useCallback(() => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="spinner">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
            <path d="M12 2C6.477 2 2 6.477 2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
        </svg>
    ), []);

    const GiftIcon = useCallback(() => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 12V22H4V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 7H2V12H22V7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 22V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C9.5 2 12 7 12 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C14.5 2 12 7 12 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ), []);

    const renderSelectionCard = useCallback((device: Device) => {
        const isSelected = selectedDevices.some((selectedDevice) => selectedDevice.id === device.id);
        const variant = getDeviceVariant(device);

        return (
            <article
                key={device.id}
                className={`device-card selectable ${variant} ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleDeviceSelection(device)}
            >
                <div className="menu-item-like">
                    <div className="device-main-copy">
                        <div className="device-name-row compact">
                            <h3 className="device-name">{getDeviceName(device)}</h3>
                            <span className={`status-dot ${variant}`} />
                        </div>

                        <div className="device-meta-row">
                            {device.protocol_name && (
                                <span className={`device-chip protocol ${variant}`}>
                                    <ProtocolIcon size={12} color={variant === 'green' ? '#57F2D3' : '#F3C969'} />
                                    {getProtocolDisplayName(device.protocol_name)}
                                </span>
                            )}

                            <span className="device-chip time">
                                {getDeviceTimeInfo(device)}
                            </span>

                            {device.server_name && (
                                <span className="device-chip server">
                                    {device.server_name}
                                </span>
                            )}
                        </div>

                        <div className="payment-device-status">{getDeviceStatus(device)}</div>
                    </div>

                    <div className="menu-right-like">
                        <div className={`device-selection-toggle ${isSelected ? 'selected' : ''}`}>
                            <span>✓</span>
                        </div>
                    </div>
                </div>
            </article>
        );
    }, [selectedDevices, getDeviceVariant, toggleDeviceSelection, getDeviceName, getProtocolDisplayName, getDeviceTimeInfo, getDeviceStatus]);

    if (isPageLoading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="payment-devices-page loading">
                    <div className="loader"><Loader /></div>
                </div>
            </div>
        );
    }

    if (pricingConfig.error) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="payment-devices-page auth-state">
                    <div className="devices-empty-state">
                        <div className="devices-empty-icon">⚙️</div>
                        <h2>Не настроены цены</h2>
                        <p>{pricingConfig.error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!telegramId && pageReady) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="payment-devices-page auth-state">
                    <div className="devices-empty-state">
                        <div className="devices-empty-icon">🔒</div>
                        <h2>Необходима авторизация</h2>
                        <p>Войдите в систему для продления подписки и управления устройствами.</p>
                        <button className="devices-primary-button" onClick={() => navigate('/key')}>
                            Перейти к авторизации
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (pageReady && (hasNoDevices || devices.length === 0)) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="payment-devices-page empty-state-page">
                    <div className="devices-empty-state">
                        <div className="devices-empty-icon">📱</div>
                        <h2>Нет устройств для продления</h2>
                        <p>У вас пока нет активных устройств. Сначала приобретите подписку.</p>
                        <button className="devices-primary-button" onClick={() => navigate('/payment')}>
                            Купить подписку
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="payment-devices-page">
                <div className="payment-shell">
                    <section className="payment-hero-card compact-header">
                        <div className="payment-hero-topline">
                            <button className="payment-back-button" onClick={() => navigate('/')}>
                                <BackIcon />
                            </button>

                            <div className="payment-hero-copy">
                                <h1 className="payment-hero-title">Продление подписки</h1>
                                <p className="payment-hero-subtitle">Выберите устройства и срок</p>
                            </div>
                        </div>
                    </section>

                    <section className="device-section profile-style selection-style">
                        <div className="section-head section-head-actions">
                            <div>
                                <div className="section-title">Выберите устройства</div>
                            </div>

                            <div className="selection-actions">
                                <button
                                    type="button"
                                    className="selection-action-btn"
                                    onClick={selectAllDevices}
                                    disabled={selectableAll}
                                >
                                    Выбрать все
                                </button>
                                <button
                                    type="button"
                                    className="selection-action-btn ghost"
                                    onClick={clearSelection}
                                    disabled={selectedCount === 0}
                                >
                                    Сбросить
                                </button>
                            </div>
                        </div>

                        <div className="devices-scroll-shell">
                            <div className="devices-grid profile-style selectable-list">
                                {sortedDevices.map(renderSelectionCard)}
                            </div>
                        </div>
                    </section>

                    {selectedCount > 0 && (
                        <section className="extra-devices-section">
                            <div className="section-head">
                                <div className="section-title">Добавить новые устройства</div>
                            </div>

                            <div className="extra-devices-grid">
                                <button
                                    className={`extra-device-btn ${extraDevices === 1 ? 'selected' : ''}`}
                                    onClick={() => setExtraDevices(prev => prev === 1 ? 0 : 1)}
                                >
                                    <span className="extra-device-count">+1</span>
                                    <span className="extra-device-label">устройство</span>
                                </button>

                                <button
                                    className={`extra-device-btn ${extraDevices === 2 ? 'selected' : ''}`}
                                    onClick={() => setExtraDevices(prev => prev === 2 ? 0 : 2)}
                                >
                                    <span className="extra-device-count">+2</span>
                                    <span className="extra-device-label">устройства</span>
                                </button>

                                <button
                                    className={`extra-device-btn ${extraDevices === 3 ? 'selected' : ''}`}
                                    onClick={() => setExtraDevices(prev => prev === 3 ? 0 : 3)}
                                >
                                    <span className="extra-device-count">+3</span>
                                    <span className="extra-device-label">устройства</span>
                                </button>
                            </div>
                        </section>
                    )}

                    {selectedCount > 0 && (
                        <section className="payment-period-block">
                            <div className="payment-period-header">
                                <div>
                                    <div className="payment-period-subtitle">
                                        Выбрано {selectedCount} {selectedCountLabel} для продления
                                    </div>
                                </div>
                            </div>

                            <div className="period-grid payment-page-style-grid">
                                {PERIOD_OPTIONS.map((period) => {
                                    const periodTotalPrice = getDisplayPrice(
                                        period.months,
                                        selectedCount,
                                        oneDevicePeriodPrices,
                                        threeDevicePeriodPrices
                                    );
                                    const oldPrice = getOldPrice(period.months, selectedCount, baseMonthPrice);
                                    const discount = getDiscountPercent(periodTotalPrice, oldPrice);

                                    return (
                                        <button
                                            key={period.months}
                                            className={`period-grid-item payment-page-style ${selectedPeriod?.months === period.months ? 'selected' : ''} ${period.popular ? 'popular' : ''}`}
                                            onClick={() => setSelectedPeriod(period)}
                                        >
                                            <div className="period-grid-content">
                                                <div className="period-grid-name">{period.name}</div>

                                                <div className="period-grid-price">
                                                    {periodTotalPrice}₽
                                                    {oldPrice > periodTotalPrice && (
                                                        <span className="period-grid-old-price">{oldPrice}₽</span>
                                                    )}
                                                </div>

                                                {discount > 0 && (
                                                    <div className="period-grid-save">
                                                        −{discount}%
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                className="continue-button payment-page-style"
                                onClick={handleContinueToPayment}
                                disabled={!selectedPeriod}
                            >
                                <span>Продолжить оформление</span>
                                <span className="continue-price">{displayPrice}₽</span>
                            </button>
                        </section>
                    )}
                </div>

                {showPaymentModal && (
                    <div className="modal-overlay" onClick={handleCloseModal}>
                        <div className="payment-modal" onClick={(event) => event.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Оплата продления</h3>
                                <button className="close-button" onClick={handleCloseModal}>✕</button>
                            </div>

                            <div className="modal-content">
                                <div className="modal-total-section">
                                    <div className="modal-total-row">
                                        <span>Устройств для продления</span>
                                        <span className="modal-value">{selectedCount}</span>
                                    </div>

                                    {extraDevices > 0 && (
                                        <div className="modal-total-row">
                                            <span>Дополнительные устройства</span>
                                            <span className="modal-value">+{extraDevices}</span>
                                        </div>
                                    )}

                                    <div className="modal-total-row">
                                        <span>Срок</span>
                                        <span className="modal-value">{selectedPeriod?.name}</span>
                                    </div>

                                    <div className="modal-total-row total">
                                        <span>Итого</span>
                                        <div className="modal-price-block">
                                            {promoApplied && totalPrice > displayPrice && (
                                                <span className="old-price">{totalPrice}₽</span>
                                            )}
                                            <span className="total-price">{displayPrice}₽</span>
                                        </div>
                                    </div>

                                    {promoApplied && promoDiscount > 0 && (
                                        <div className="modal-discount-row">
                                            <span>Скидка по промокоду</span>
                                            <span className="discount-value">−{promoDiscount}%</span>
                                        </div>
                                    )}

                                    {selectedPeriodTariffDiscount > 0 && (
                                        <div className="modal-bonus">
                                            <span className="bonus-icon"><GiftIcon /></span>
                                            <span>Экономия по тарифу {selectedPeriodTariffDiscount}%</span>
                                        </div>
                                    )}
                                </div>

                                <div className="modal-promo-section">
                                    <div className="promo-input-group">
                                        <input
                                            type="text"
                                            className="promo-input"
                                            placeholder="Введите промокод"
                                            value={promoCode}
                                            onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                                            disabled={promoApplied || processing}
                                        />
                                        <button
                                            className={`promo-button ${promoApplied ? 'applied' : ''}`}
                                            onClick={handleApplyPromo}
                                            disabled={!promoCode.trim() || promoApplied || applyingPromo || processing}
                                        >
                                            {applyingPromo ? '⏳' : promoApplied ? '✓' : 'Применить'}
                                        </button>
                                    </div>

                                    {promoMessage && (
                                        <div className={`promo-message ${promoApplied ? 'success' : 'error'}`}>
                                            {promoMessage}
                                        </div>
                                    )}
                                </div>
                                <div className="modal-payment-methods">
                                    <div className="modal-payment-title">Способ оплаты</div>
                                    <div className="modal-payment-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                                        {/*{['card-sbp', 'crypto'].map((method) => (*/}
                                        {['card', 'sbp'].map((method) => (
                                            <button
                                                key={method}
                                                className={`modal-payment-option ${selectedPaymentMethod === method ? 'selected' : ''}`}
                                                onClick={() => setSelectedPaymentMethod(method)}
                                                disabled={processing}
                                            >
                                                <span className="method-icon">{getPaymentMethodIcon(method)}</span>
                                                <span className="method-name">{getPaymentMethodName(method)}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {paymentError && (
                                    <div className="payment-error">{paymentError}</div>
                                )}

                                <button
                                    className="modal-pay-button"
                                    onClick={handlePay}
                                    disabled={processing}
                                >
                                    {processing ? (
                                        <>
                                            <span>Обработка</span>
                                            <span><LoaderIcon /></span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Оплатить</span>
                                            <div className="pay-button-prices">
                                                {promoApplied && displayPrice !== totalPrice && (
                                                    <span className="old-price-button">{totalPrice}₽</span>
                                                )}
                                                <span className="final-price-button">{displayPrice}₽</span>
                                            </div>
                                        </>
                                    )}
                                </button>

                                <div className="modal-footer">
                                    <div className="security-info">
                                        <span className="security-icon">🔒</span>
                                        Безопасный платеж через ЮKassa
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PaymentDevicesPage;